import { useEffect, useRef, useState } from 'react';
import { useUnderstanding } from '@/hooks/useConsoleData';
import { Link } from 'react-router-dom';
import { NOT_MEASURED, num, plural, when } from '@/lib/format';
import { durationLabel, endingLine, tracePlatformLabel } from '@/lib/session-data';
import {
  HABIT_DAY_COLUMNS,
  blockedLines,
  cellValue,
  feltLabel,
  habitDayCell,
  reasonLines,
  zoneSourceLabel,
  type MemberUnderstanding,
} from '@/lib/monitoring-data';
import s from '@/pages/console/console.module.css';

/**
 * One member's Understanding (pv-auth GET /provider/admin/people/:id/understanding):
 * the plain summary the server computed from the rows, then the rows
 * themselves. Nothing here is inferred: a column nobody measured says "not
 * measured", a day nobody told says "not told", and a table that is not on
 * the server yet says "not built yet". The member words shown are only the
 * ElinaKnowledge values the person page already lists.
 */

const Sub = ({ title, children, note }: { title: string; children: React.ReactNode; note?: React.ReactNode }) => (
  <div style={{ marginTop: 14 }}>
    <div className={s.groupTitle}>{title}</div>
    {note && <div className={s.sub} style={{ marginBottom: 6 }}>{note}</div>}
    {children}
  </div>
);

function Evidence({ value }: { value: unknown }) {
  const [open, setOpen] = useState(false);
  if (value === null || value === undefined) return <span className={s.missing}>{NOT_MEASURED}</span>;
  return (
    <>
      <button type="button" className={s.linkBtn} onClick={() => setOpen((v) => !v)} aria-expanded={open}>{open ? 'Hide' : 'Show'}</button>
      {open && <pre className={s.sub} style={{ whiteSpace: 'pre-wrap', margin: '4px 0 0' }}>{JSON.stringify(value, null, 2)}</pre>}
    </>
  );
}

export function Understanding({ data }: { data: MemberUnderstanding }) {
  const u = data;
  const pick = u.planPick.today ?? u.planPick.latest;
  return (
    <div data-panel="understanding">
      <p className={s.whyLine} data-summary style={{ margin: 0 }}>{u.summary}</p>
      <div className={s.sub} style={{ marginTop: 6 }} data-timezone>
        Timezone {u.timezone.resolved}, from {zoneSourceLabel(u.timezone.source)}
        {u.timezone.storedAt ? ` · written ${when(u.timezone.storedAt)}` : ''}
        {u.timezone.fromRowsAt ? ` · that row ${when(u.timezone.fromRowsAt)}` : ''}
      </div>
      {u.unavailable.length > 0 && <div className={s.jWarn}>Not read this time: {u.unavailable.join(', ')}.</div>}

      <Sub title="Habit days, the last 28" note={u.habitDays.length ? `${plural(u.habitDays.length, 'day')} rolled up, ${u.counts.habitDaysMeasured} with something measured, newest first` : undefined}>
        {u.habitDays.length === 0 ? (
          <div className={s.empty}>No HabitDay row yet: the nightly roll-up is off or has not reached this person.</div>
        ) : (
          <div className={s.tableWrap} data-table="habit-days">
            <table className={s.table}>
              <thead><tr>{HABIT_DAY_COLUMNS.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
              <tbody>
                {u.habitDays.map((d) => (
                  <tr key={String(d.localDate)} data-day={String(d.localDate)}>
                    {HABIT_DAY_COLUMNS.map((c) => {
                      const v = habitDayCell(c.key, d[c.key]);
                      return <td key={c.key} className={v === NOT_MEASURED || v === 'not told' ? s.missing : undefined} style={{ whiteSpace: c.key === 'stressors' || c.key === 'feelTags' || c.key === 'driftByBlock' ? 'normal' : 'nowrap', minWidth: c.key === 'stressors' ? 220 : undefined }}>{v}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Sub>

      <Sub title="Habit findings" note="shadow is computed and shown to nobody while HABITS_V1 is off">
        {u.habitFindings.length === 0 ? <div className={s.empty}>No finding yet.</div> : (
          <div className={s.tableWrap} data-table="findings">
            <table className={s.table}>
              <thead><tr><th>Habit</th><th>State</th><th>Evidence days</th><th>First seen</th><th>Last seen</th><th>Answer</th><th>Evidence</th></tr></thead>
              <tbody>
                {u.habitFindings.map((f) => (
                  <tr key={String(f.habitKey)} data-finding={String(f.habitKey)}>
                    <td>{String(f.habitKey).replace(/_/g, ' ')}</td>
                    <td><span className={s.pill}>{cellValue(f.state)}</span></td>
                    <td>{cellValue(f.evidenceDays)} of {cellValue(f.windowDays)}</td>
                    <td>{when(f.firstSeenAt as string | null)}</td>
                    <td>{when(f.lastSeenAt as string | null)}</td>
                    <td>{f.helpedAnswer ? cellValue(f.helpedAnswer) : f.dismissedAt ? 'not for me' : f.retiredAt ? 'eased' : <span className={s.missing}>no answer</span>}</td>
                    <td><Evidence value={f.evidence} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Sub>

      <Sub title={`Plan pick for ${u.planPick.localDate}`}>
        {!u.planPick.built ? (
          <div className={s.empty} data-plan="not-built">Not built yet: the PlanPick table arrives with pv-auth #409.</div>
        ) : !pick ? (
          <div className={s.empty} data-plan="none">No pick stored for this person yet.</div>
        ) : (
          <div data-plan={u.planPick.today ? 'today' : 'latest'}>
            {!u.planPick.today && <div className={s.jWarn}>No pick stored for today; the newest one is from {cellValue(pick.localDate)}.</div>}
            <dl className={s.facts}>
              <dt>The one thing</dt><dd>{pick.activationId ? cellValue(pick.activationId) : 'nothing survived the rules'}</dd>
              <dt>Top reason</dt><dd>{pick.reasonKey ? cellValue(pick.reasonKey) : <span className={s.missing}>none fired</span>}</dd>
              <dt>Every reason</dt><dd>{reasonLines(pick.reasons).length ? reasonLines(pick.reasons).join('; ') : <span className={s.missing}>none fired</span>}</dd>
              <dt>Blocked ids</dt><dd data-blocked>{blockedLines(pick.blocked).length ? blockedLines(pick.blocked).join('; ') : 'none'}</dd>
              <dt>Why line</dt><dd>{cellValue(pick.whyLine)}</dd>
              <dt>Trigger</dt><dd>{cellValue(pick.trigger)} · computed {when(pick.computedAt as string | null)}{pick.startedAt ? ` · started ${when(pick.startedAt as string)}` : ''}</dd>
            </dl>
          </div>
        )}
      </Sub>

      <Sub title="What Elina was told, by kind" note={u.knowledgeCapped ? 'the newest 200 rows, the same cap as the person page' : undefined}>
        {u.knowledge.map((g) => (
          <div key={g.key} data-knowledge={g.key} style={{ marginBottom: 8 }}>
            <div className={s.sub}><strong>{g.label}</strong> · {g.rows.length ? plural(g.rows.length, 'row') : g.built ? 'none stored' : 'not built yet'}</div>
            {g.rows.length > 0 && (
              <dl className={s.facts}>
                {g.rows.map((r, i) => (
                  <div key={`${r.kind}-${r.at}-${i}`} style={{ display: 'contents' }}>
                    <dt>{r.kind.replace(/_/g, ' ')}</dt>
                    <dd>{r.valueText ?? (r.valueInt !== null ? String(r.valueInt) : <span className={s.missing}>no value</span>)} <span className={s.sub}>· {r.source ?? 'source not recorded'} · {when(r.at)}</span></dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        ))}
      </Sub>

      <Sub title="Activations" note={`${u.counts.activations === null ? NOT_MEASURED : plural(u.counts.activations, 'run')} in all; the newest ${u.activations.length} below with the felt answer`}>
        {u.activations.length === 0 ? <div className={s.empty}>No activation yet.</div> : (
          <div className={s.tableWrap} data-table="activations">
            <table className={s.table}>
              <thead><tr><th>When</th><th>Activation</th><th>Done</th><th>Reps</th><th>Score</th><th>Steadiness</th><th>Camera saw</th><th>Felt</th></tr></thead>
              <tbody>
                {u.activations.map((a) => (
                  <tr key={a.id} data-activation={a.id}>
                    <td>{when(a.recordedAt)}</td>
                    <td>{a.exerciseName}</td>
                    <td>{a.completed ? 'completed' : 'partial'}</td>
                    <td>{a.unit === 'duration' ? `hold ${cellValue(a.holdSeconds)} s` : `${a.completedReps} of ${a.targetReps}`}</td>
                    <td>{a.formScored === false ? 'form not scored' : cellValue(a.score)}</td>
                    <td>{cellValue(a.stabilityScore)}</td>
                    <td>{a.trackableFramesRatio === null ? NOT_MEASURED : `${Math.round(a.trackableFramesRatio * 100)}%`}</td>
                    <td className={a.felt ? undefined : s.missing}>{feltLabel(a.felt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Sub>

      <Sub title="Active Mode sessions" note={`${u.counts.activeSessions === null ? NOT_MEASURED : plural(u.counts.activeSessions, 'session')} in all${u.counts.meetingWindows ? ` · ${plural(u.counts.meetingWindows, 'call')} seen by meeting watch` : ''}`}>
        {u.activeSessions.length === 0 ? <div className={s.empty}>No Active Mode session yet.</div> : (
          <div className={s.tableWrap} data-table="active-sessions">
            <table className={s.table}>
              <thead><tr><th>Day</th><th>Started</th><th>Minutes watched</th><th>Drift</th><th>Cues</th><th>Camera</th><th>Ended</th><th>Meeting windows</th></tr></thead>
              <tbody>
                {u.activeSessions.map((a) => (
                  <tr key={a.sessionId} data-am={a.sessionId}>
                    <td>{a.localDate}</td>
                    <td>{when(a.startedAt)}</td>
                    <td>{cellValue(a.monitoredMinutes)}</td>
                    <td>{a.avgDriftPct === null ? NOT_MEASURED : `${cellValue(a.avgDriftPct)}%`}</td>
                    <td>{a.cueCount === null ? NOT_MEASURED : `${a.cueCount}, ${cellValue(a.cuesCorrected)} corrected`}</td>
                    <td>{cellValue(a.cameraBand)}</td>
                    <td>{a.endReason ? a.endReason.replace(/_/g, ' ') : 'still open'}</td>
                    <td style={{ whiteSpace: 'normal' }}>{a.meetingWindows.length ? a.meetingWindows.map((w) => `${when(w.startAt)} to ${new Date(w.endAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`).join('; ') : 'none'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Sub>

      <Sub title="Session traces" note="every engine session the client posted a trace for; open one for the autopsy">
        {u.traces.length === 0 ? <div className={s.empty}>No session trace yet.</div> : (
          <div className={s.list} data-table="traces">
            {u.traces.map((t) => (
              <div key={t.id} className={s.sub} data-trace={t.peerId}>
                {when(t.startedAt)} · {endingLine(t)} · {durationLabel(t.durationS)} · {tracePlatformLabel(t.platform)} · {t.result ? `result score ${t.result.score === null ? NOT_MEASURED : Math.round(t.result.score)}` : 'no result row'}{' · '}
                <Link className={s.linkA} to={`/people/${encodeURIComponent(t.userId)}/sessions/${encodeURIComponent(t.peerId)}`}>Open the session page</Link>
              </div>
            ))}
            <Link className={s.linkA} to={`/people/${encodeURIComponent(u.person.userId)}/sessions`}>Every session</Link>
          </div>
        )}
      </Sub>

      <div className={s.sub} style={{ marginTop: 12 }}>Checks {num(u.counts.checks)} on {num(u.counts.checkDays)} days · told days {num(u.counts.toldDays)} · calendar {u.counts.calendarConnected === null ? NOT_MEASURED : u.counts.calendarConnected ? 'connected' : 'not connected'} · read {when(u.generatedAt)}</div>
    </div>
  );
}

/**
 * The person page's Understanding drop-down. The read is made only once the
 * drop-down is open, because every read writes an access-log row; it opens
 * on arrival from "Members to learn from" (#understanding).
 */
export function UnderstandingSection({ userId, defaultOpen = false }: { userId: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const q = useUnderstanding(open ? userId : null);
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => { if (defaultOpen) ref.current?.scrollIntoView?.({ block: 'start' }); }, [defaultOpen]);
  return (
    <details ref={ref} className={s.section} open={defaultOpen} id="understanding" onToggle={(e) => { if ((e.currentTarget as HTMLDetailsElement).open) setOpen(true); }}>
      <summary>
        <span className={s.sectionTitle}>Understanding</span>
        <span className={s.sectionSummary}>what we monitor and save for this person, and what we are still counting</span>
      </summary>
      <div className={s.sectionBody}>
        {q.isError && <div className={s.error}>Could not load the Understanding. This needs GET /provider/admin/people/:id/understanding (pv-auth #411).</div>}
        {open && q.isLoading && <div className={s.sub}>Loading what we hold.</div>}
        {q.data && <Understanding data={q.data} />}
      </div>
    </details>
  );
}
